import { Box, Stack, Typography } from '@mui/material';
import { DataGridProProps, GridFilterModel } from '@mui/x-data-grid-pro';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Divider } from '@/components/Divider';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingBanner } from '@/components/notifications/LoadingBanner/LoadingBanner';
import { PageSizes } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { usePaginatedDataTableQuery } from '@/components/tables/DataTable/hooks/usePaginatedDataTableQuery';
import { convertOrderToSortModel } from '@/components/tables/DataTable/utils/sortModelUtils';
import { useReportError } from '@/hooks/useReportError';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { shouldPreopenOnboardingModal } from '@/modules/createHousehold/CreateHouseholdFlow/CreateHouseholdFlow';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { SmartReviewOnboardingModal } from '@/modules/onboarding/smartReview/SmartReviewOnboardingModal';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';

import { ClientListEmptyState } from './ClientListEmptyState';
import {
  CreateClientButton,
  ManageExternalDataButton,
} from './ClientListPage.components';
import { getFilterVariables, mapDataToRows } from './ClientListPage.utils';
import { ClientSearchFilterToolbar } from './ClientSearchFilterToolbar/ClientSearchFilterToolbar';
import { useClientListPageContext } from './context/ClientListPage.context';
import { ClientListPageProvider } from './context/ClientListPage.provider';
import { useClientListTeamCountQuery } from './graphql/ClientListTeamCount.generated';
import { useListClientsQuery } from './graphql/GetClients.generated';
import {
  columnFieldToSortField,
  useClientListPageSort,
} from './hooks/useClientListPageSort';
import { useColumns } from './hooks/useColumns';
import { useHouseholdCopyTracker } from './hooks/useHouseholdCopyTracker';

const VALIDATION_STATUS_REFRESH_INTERVAL_MS = 180_000;

function ClientListPageInner() {
  const navigate = useNavigate();
  const [searchString, setSearchString] = useState('');
  const { filterValue } = useClientListPageContext();
  const { hasSubBrands, displayName: defaultTenantDisplayName } =
    useTenantDetailsContext();
  const { handleSortModelChange, getEffectiveOrderBy } =
    useClientListPageSort();
  const currentUser = useCurrentUser();
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');
  const isCollaborator = currentUser?.collaborator !== null;

  const onboardingModalInitialState = shouldPreopenOnboardingModal();
  const smartReviewOnboardingTrowser = useTrowser(
    TrowserKey.SmartReviewOnboarding
  );
  const hasHandledPreopenRef = useRef(false);

  useEffect(() => {
    if (hasHandledPreopenRef.current || !onboardingModalInitialState) {
      return;
    }
    hasHandledPreopenRef.current = true;
    smartReviewOnboardingTrowser.open();
  }, [onboardingModalInitialState, smartReviewOnboardingTrowser]);

  const openCreateHouseholdModal = () => {
    smartReviewOnboardingTrowser.open();
  };

  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const onFilterModelChange = ({ quickFilterValues }: GridFilterModel) => {
    const searchString: string = quickFilterValues?.[0];
    setSearchString(searchString ?? '');
  };

  const [paginatedTableProps, { data, refetch }] = usePaginatedDataTableQuery(
    useListClientsQuery,
    {
      skip: !currentUser,
      pollInterval: enhanced ? VALIDATION_STATUS_REFRESH_INTERVAL_MS : 0,
      variables: {
        query: searchString,
        where: {
          hasClientProfiles: true,
          ...getFilterVariables(filterValue),
        },
        orderBy: getEffectiveOrderBy(),
        skipTeams: isCollaborator,
        includeValidationStatus: enhanced,
      },
      pageSize: PageSizes.Twenty,
      onError: (error) => {
        reportError('failed to fetch clients list', error);
        showFeedback(
          'Failed to load clients. Please refresh the page to try again.'
        );
      },
    }
  );

  // Gate the Teams column on whether the tenant has any teams. Collaborators
  // never see the column, so skip the request for them. Default to showing the
  // column until the count resolves as zero, so a slow/failed query doesn't hide
  // teams for a tenant that has them.
  const { data: teamCountData } = useClientListTeamCountQuery({
    skip: isCollaborator,
  });
  const hasTeams = teamCountData ? teamCountData.teams.totalCount > 0 : true;

  const { isCopying } = useHouseholdCopyTracker({
    onCompleted: () => {
      refetch();
    },
  });

  const rows = mapDataToRows(data ?? []);
  const columns = useColumns({
    showSubBrand: hasSubBrands,
    defaultTenantDisplayName,
    hasTeams,
  });

  const onRowClick: DataGridProProps['onRowClick'] = (params, event) => {
    const householdId = params.id;
    const clientDetailsPath = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
      { householdId: householdId.toString() }
    );

    if (event.metaKey) {
      window.open(clientDetailsPath, '_blank');
      return;
    }

    navigate(clientDetailsPath);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <Typography variant="title-xl">Clients</Typography>
        <Stack direction="row" spacing={1}>
          <ManageExternalDataButton />
          <CreateClientButton onClick={openCreateHouseholdModal} />
        </Stack>
      </Stack>
      <Divider
        sx={{
          borderColor: COLORS.GRAY[200],
        }}
      />
      <Stack p={2} gap={2}>
        {isCopying && (
          <LoadingBanner
            content={
              <Typography variant="title-xs">Copying household</Typography>
            }
          />
        )}
        <Stack flex={1}>
          <Box data-testid="ClientListTable">
            <DataTable
              {...paginatedTableProps}
              rows={rows}
              columns={columns}
              onRowClick={onRowClick}
              slots={{
                toolbar: ClientSearchFilterToolbar,
                noRowsOverlay: () => (
                  <ClientListEmptyState
                    searchString={searchString}
                    filterValue={filterValue}
                    onCreateClient={openCreateHouseholdModal}
                  />
                ),
              }}
              onFilterModelChange={onFilterModelChange}
              filterMode="server"
              sortingMode="server"
              sortingOrder={['asc', 'desc']}
              onSortModelChange={handleSortModelChange}
              // Keep sort indicators in sync with the query. When the user clears the
              // sort, the query still falls back to the default sort via
              // `getEffectiveOrderBy()`. Using it here ensures consistent behaviour.
              sortModel={convertOrderToSortModel(
                getEffectiveOrderBy(),
                (() => {
                  const mapping: Record<string, string> = {};
                  Object.entries(columnFieldToSortField).forEach(
                    ([key, value]) => {
                      mapping[value] = key;
                    }
                  );
                  return mapping;
                })()
              )}
            />
          </Box>
        </Stack>
      </Stack>

      {smartReviewOnboardingTrowser.isOpen && (
        <SmartReviewOnboardingModal
          isOpen
          onClose={smartReviewOnboardingTrowser.close}
        />
      )}
    </>
  );
}

export function ClientListPage() {
  return (
    <ClientListPageProvider>
      <ClientListPageInner />
    </ClientListPageProvider>
  );
}
